import { useState } from 'react';
import { Share2, X, Copy, Check } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useToast, useCopy } from './Toast';
import type { AlertKind, ShareTTL } from '@shared/types';

const TTL_OPTIONS: { value: ShareTTL; label: string }[] = [
  { value: '10m', label: '10 min' },
  { value: '1h', label: '1 ora' },
  { value: '24h', label: '24 ore' },
];

/**
 * Condivisione sicura (§6.13) — bottom sheet sopra la schermata di dettaglio.
 * Genera un link pubblico read-only con TTL e PIN opzionale.
 */
export function ShareSheet({
  resourceType,
  resourceId,
  name,
  onClose,
}: {
  resourceType: AlertKind;
  resourceId: string;
  name: string;
  onClose: () => void;
}) {
  const { show } = useToast();
  const copy = useCopy();
  const [ttl, setTtl] = useState<ShareTTL>('1h');
  const [pinOn, setPinOn] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const generate = async () => {
    if (busy) return;
    if (pinOn && pin.trim().length < 4) return show('Imposta un PIN di almeno 4 cifre');
    setBusy(true);
    try {
      const res = await api.createShare({
        resource_type: resourceType,
        resource_id: resourceId,
        ttl,
        pin: pinOn ? pin.trim() : null,
      });
      setLink(res.url);
      show('Link sicuro generato');
    } catch (e) {
      show(e instanceof ApiError ? e.message : 'Generazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        <div className="sheet__head">
          <span className="iconchip iconchip--sm">
            <Share2 size={18} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Condivisione sicura</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{name}</div>
          </div>
          <button className="iconbtn" onClick={onClose} aria-label="Chiudi" style={{ width: 34, height: 34 }}>
            <X size={18} />
          </button>
        </div>

        {/* Scade dopo */}
        <div className="eyebrow" style={{ marginTop: 8, marginBottom: 9 }}>Scade dopo</div>
        <div className="chips">
          {TTL_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`chip${ttl === o.value ? ' is-active' : ''}`}
              onClick={() => setTtl(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Proteggi con PIN */}
        <div className="optionrow" style={{ marginTop: 16 }}>
          <div className="optionrow__body">
            <div className="optionrow__title">Proteggi con PIN</div>
            <div className="optionrow__sub">Richiede un codice all'apertura del link</div>
          </div>
          <button
            type="button"
            className={`switch${pinOn ? ' is-on' : ''}`}
            onClick={() => setPinOn((v) => !v)}
            aria-pressed={pinOn}
            aria-label="Proteggi con PIN"
          >
            <span className="switch__knob" />
          </button>
        </div>
        {pinOn && (
          <input
            className="field__input"
            style={{ marginTop: 10 }}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
            inputMode="numeric"
            placeholder="PIN del link (min 4 cifre)"
          />
        )}

        {/* Link generato */}
        {link && (
          <div className="sharelink" style={{ marginTop: 16 }}>
            <span className="sharelink__url">{link}</span>
            <button className="sharelink__copy" onClick={() => copy(link, 'Link copiato')} aria-label="Copia link">
              <Copy size={16} />
            </button>
          </div>
        )}

        <button className="btn btn--accent" style={{ width: '100%', marginTop: 18 }} onClick={generate} disabled={busy}>
          {link ? <><Check size={18} /> Rigenera link</> : <><Share2 size={18} /> Genera link sicuro</>}
        </button>
      </div>
    </div>
  );
}
